import { TableActionButton } from "_shared/_views/tables/table-toolbar";
import AddCommentIcon from "@mui/icons-material/AddComment";
import useSessionPermissionsLoadState from "permissions/session/use-session-permissions-load-state";
import CloseIcon from "@mui/icons-material/Close";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import MessageIcon from "@mui/icons-material/Message";
import PageviewIcon from "@mui/icons-material/Pageview";
import SearchIcon from "@mui/icons-material/Search";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { styled, Theme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import RowActions from "_shared/_views/tables/row-actions";
import RowLink from "_shared/_views/tables/row-link";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import Guid from "_shared/guids/guid";
import {
  createMRTColumnHelper,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
  type MRT_TableOptions,
  MRT_TableInstance as TableInstance,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
} from "material-react-table";
import {
  ToolbarButtons,
  searchFieldSpacing,
} from "_shared/_views/tables/table-toolbar";
import { TableToolsMenu } from "_shared/_views/tables/table-tools-menu";
import { ListPageToolbarButtons } from "_shared/_views/list-pages/list-page-heading";
import { CONVERSATIONS_PATH } from "notifications/_views/communications-list-chips";
import ForumQuery, {
  ForumFilterParameters,
  ForumOrderField,
  ForumOrderParameters,
  unreadForumsSearchParameter,
} from "messaging/forums/_api/queries/forum-query";
import {
  getErrorLoadingForumsByQuery,
  getForumQueryByInterface,
  getForumsByQuery,
  getIsLoadingForumsByQuery,
  getIsStaleForumsByQuery,
  getTotalForumsByQuery,
  populateForumsByQuery,
  updateForumInterfaceQuery,
} from "messaging/forums/_stores/forums-redux-slice";
import { recordConversationPath } from "messaging/conversations/conversation-links";
import Forum from "messaging/forums/forum";
import { getForumChatSubtitle, getForumChatTitle } from "messaging/forums/forum-chat-display";
import { getProposalStatusLabel } from "work/proposals/proposal-status-display";
import ForumName from "notifications/message-notifications/forum-info/forum-name";
import { getSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetProposalsByIdsQuery } from "work/proposals/_api/proposal-api";
import { useGetWorkAgreementsByIdsQuery } from "work/work-agreements/_api/work-agreement-api";
import WorkVersion from "work/work-agreements/versions/work-version";
import { TimestampCell } from "_shared/_views/tables/date-cells";
import { LIST_PAGE_SIZE } from "_shared/_views/tables/table-page-size";

const ToolbarActionButtons = styled("div")(() => ({
  textAlign: "right",
  whiteSpace: "nowrap",
}));
const CategoryItem = styled(ListItem)(({ theme }) => ({
  margin: 0,
  padding: 0,
  minWidth: 0,
  "& .MuiListItemText-root": {
    minWidth: 0,
  },
  "& .MuiListItemText-primary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiListItemText-secondary": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  [theme.breakpoints.down("md")]: {
    "& .MuiListItemIcon-root": {
      display: "none",
    },
  },
}));
const AlertActions = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "nowrap",
  height: "100%",
  paddingRight: theme.spacing(2),
  whiteSpace: "nowrap",
}));

type ConversationTableProps = {
  renderTopToolbarCustomActions: (props: { table: TableInstance<Forum> }) => React.ReactNode;
  /** Opens the compose dialog over the Communications page's Conversations tab (#6719). */
  onCompose: () => void;
};

const columnHelper = createMRTColumnHelper<Forum>();

export default function ConversationTable(props: Readonly<ConversationTableProps>) {
  const { renderTopToolbarCustomActions, onCompose } = props;
  const [rowSelection, setRowSelection] = React.useState<Record<string, boolean>>({});

  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<Array<{ id: string; desc: boolean }>>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: LIST_PAGE_SIZE,
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // The Unread chip's state lives in the URL, so a reload, the back button and a link from the
  // dashboard all land on the same list (#6583).
  const unread = searchParams.get(unreadForumsSearchParameter) === "true";
  // A different filter is a different list: its first page, not the page the last one was on.
  const [unreadOfPage, setUnreadOfPage] = useState(unread);
  if (unread !== unreadOfPage) {
    setUnreadOfPage(unread);
    setPagination((current) => ({ ...current, pageIndex: 0 }));
  }
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const permissionsLoadState = useSessionPermissionsLoadState();
  const isMedDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const interfaceName = "forumsIndex";

  const dispatch = useAttorneyHubDispatch();

  const forumQuery = useSelector(getForumQueryByInterface(interfaceName));
  const forums = useSelector(getForumsByQuery(forumQuery));
  const queryIsLoading = useSelector(getIsLoadingForumsByQuery(forumQuery));
  const errorLoadingQuery = useSelector(getErrorLoadingForumsByQuery(forumQuery));
  const queryIsStale = useSelector(getIsStaleForumsByQuery(forumQuery));
  const totalForums = useSelector(getTotalForumsByQuery(forumQuery));
  // A page the store has not loaded yet has neither rows nor a total, so the last count stands until
  // it arrives rather than the pager reading "0 of 0" and disabling itself mid-page-turn.
  const [rowCount, setRowCount] = useState(0);
  const loadedRowCount = totalForums ?? forums?.length;
  if (loadedRowCount !== undefined && loadedRowCount !== rowCount) setRowCount(loadedRowCount);

  const proposalIds = useMemo(
    () =>
      (forums ?? [])
        .filter((forum) => forum.topic?.entityClass?.value === "Work.Proposal")
        .map((forum) => forum.topic?.entityId?.value)
        .filter((id): id is string => Boolean(id)),
    [forums],
  );
  const workAgreementIds = useMemo(
    () =>
      (forums ?? [])
        .filter((forum) => forum.topic?.entityClass?.value === "Work.Agreement")
        .map((forum) => forum.topic?.entityId?.value)
        .filter((id): id is string => Boolean(id)),
    [forums],
  );
  const proposalsQuery = useGetProposalsByIdsQuery(
    { proposalIds: [...new Set(proposalIds)] },
    { skip: proposalIds.length === 0 },
  );
  const workAgreementsQuery = useGetWorkAgreementsByIdsQuery(
    { workAgreementIds: [...new Set(workAgreementIds)] },
    { skip: workAgreementIds.length === 0 },
  );
  const forumEntityNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const proposal of proposalsQuery.data?.found ?? []) {
      if (proposal.id && proposal.name) names.set(`Work.Proposal:${proposal.id}`, proposal.name);
    }
    for (const workAgreement of workAgreementsQuery.data?.found ?? []) {
      if (workAgreement.id && workAgreement.name) names.set(`Work.Agreement:${workAgreement.id}`, workAgreement.name);
    }
    return names;
  }, [proposalsQuery.data, workAgreementsQuery.data]);

  // A work agreement and a proposal say which version of the work they are, since a change order
  // keeps the project's name (#6809).
  const forumEntityVersions = useMemo(() => {
    const versions = new Map<string, string>();
    for (const proposal of proposalsQuery.data?.found ?? []) {
      const version = WorkVersion.fromNumbers(proposal)?.label;
      if (proposal.id && version) versions.set(`Work.Proposal:${proposal.id}`, version);
    }
    for (const workAgreement of workAgreementsQuery.data?.found ?? []) {
      const version = WorkVersion.ofConversation(workAgreement)?.label;
      if (workAgreement.id && version) versions.set(`Work.Agreement:${workAgreement.id}`, version);
    }
    return versions;
  }, [proposalsQuery.data, workAgreementsQuery.data]);

  // Only proposals carry a stage worth showing here; a work agreement has been hired and has
  // nowhere left to be (#5129). A proposal the reader may not open comes back as an error with no
  // status, and so contributes nothing.
  const forumEntityStatuses = useMemo(() => {
    const statuses = new Map<string, string>();
    for (const proposal of proposalsQuery.data?.found ?? []) {
      const statusLabel = getProposalStatusLabel(proposal.status);
      if (proposal.id && statusLabel) statuses.set(`Work.Proposal:${proposal.id}`, statusLabel);
    }
    return statuses;
  }, [proposalsQuery.data]);

  const showAlertBanner = Boolean(errorLoadingQuery);
  const showProgressBars = queryIsLoading;
  const showSkeletons = queryIsLoading && forums === undefined && !errorLoadingQuery;

  const defaultQuery = React.useMemo(
    () =>
      new ForumQuery({
        filterParameters: new ForumFilterParameters({}),
        orderParameters: new ForumOrderParameters({
          orderBy: ForumOrderField.CreatedDate,
          orderDir: OrderDirection.Descending,
        }),
        paginationParameters: PaginationParameters.create({
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
        }),
      }),
    [pagination.pageIndex, pagination.pageSize]
  );


  useEffect(() => {
    if (!forumQuery) {
      dispatch(updateForumInterfaceQuery({ interfaceName, query: defaultQuery }));
      return;
    }

    // A failure has to stop this, or it does not stop at all: a rejected fetch leaves the forums
    // undefined and the loading flag false, which is exactly the condition to fetch again, so the
    // table sat on skeleton rows re-asking a server that had already said no. The communications
    // page checks its own error for the same reason, in `shouldPopulateNotificationQuery`. The
    // retry in the empty state is then the way back, and it clears the error as it goes.
    // A stale list of unread conversations keeps its rows on screen while it is asked again.
    if (forumQuery && (forums === undefined || queryIsStale) && !queryIsLoading && !errorLoadingQuery) {
      dispatch(
        populateForumsByQuery({
          session: session,
          query: forumQuery,
        }),
      );
    }
  }, [forums, forumQuery, globalFilter, queryIsLoading, queryIsStale, errorLoadingQuery, dispatch, defaultQuery, session]);

  useEffect(() => {
    const forumNameFilter = ForumName.fromJSON(columnFilters.find((f) => f.id === "name")?.value);
    const filterParameters = new ForumFilterParameters({
      name: forumNameFilter ?? undefined,
      query: globalFilter || undefined,
      unread: unread || undefined,
    });
    let orderBy = ForumOrderField.CreatedDate;
    let orderDir = OrderDirection.Descending;
    if (sorting.length > 0) {
      const sort = sorting[0];
      orderBy = getForumOrderField(sort.id);
      orderDir = sort.desc ? OrderDirection.Descending : OrderDirection.Ascending;
    }
    const orderParameters = new ForumOrderParameters({ orderBy, orderDir });
    const newQuery = new ForumQuery({
      filterParameters,
      orderParameters,
      paginationParameters: PaginationParameters.create({
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      }),
    });
    if (newQuery.isEqualTo(forumQuery)) return;
    dispatch(
      updateForumInterfaceQuery({
        interfaceName: "forumsIndex",
        query: newQuery,
      }),
    );
  }, [columnFilters, dispatch, forumQuery, globalFilter, pagination, sorting, unread]);

  async function navigateToTarget(forum: Forum) {
    if (forum?.id) {
      navigate(conversationPath(forum));
    }
  }

  const renderConversationNameColumn = useCallback((forum: Forum, table: TableInstance<Forum>): React.ReactNode => {
    const entityClass = forum.topic?.entityClass?.value;
    const entityId = forum.topic?.entityId?.value;
    const entityKey = entityClass && entityId ? `${entityClass}:${entityId}` : undefined;
    const entityName = entityKey ? forumEntityNames.get(entityKey) : undefined;
    // The proposals come back in their own request, seconds after the forums on a cold start, so
    // say the row is waiting rather than leave the stage silently missing (#5129).
    const knownStatus = entityKey ? forumEntityStatuses.get(entityKey) : undefined;
    const isAwaitingProposal = entityClass === "Work.Proposal" && proposalsQuery.isLoading;
    const entityStatus = knownStatus ?? (isAwaitingProposal ? "Loading..." : undefined);
    const entityVersion = entityKey ? forumEntityVersions.get(entityKey) : undefined;

    return (
      <CategoryItem dense>
        <ListItemIcon>
          <MessageIcon />
        </ListItemIcon>
        <ListItemText
          primary={
            <RowLink to={forum.id ? conversationPath(forum) : undefined}>
              {/* The title is the forum's name, which the search covers; the line under it is not (#6590). */}
              <SearchMatch text={getForumChatTitle(forum, session.user?.fullName)} table={table} />
            </RowLink>
          }
          secondary={getForumChatSubtitle(forum, entityName, session.user?.fullName, entityStatus, entityVersion)}
          slotProps={{
            primary: {
              sx: {
                fontWeight: "500"
              }
            },
            secondary: {
              sx: {
                fontSize: "0.75rem",
              },
            },
          }}
        />
      </CategoryItem>
    );
  }, [forumEntityNames, forumEntityStatuses, forumEntityVersions, proposalsQuery.isLoading, session.user?.fullName]);

  const renderCreatedDateColumn = useCallback((forum: Forum): React.ReactNode => {
    return <TimestampCell value={forum.createdDate} />;
  }, []);

  const columns = useMemo(() => [
    columnHelper.accessor(getForumNameFilterValue, {
      header: "Conversation name",
      id: "name",
      meta: { kind: "name" },
      enableSorting: true,
      enableColumnFilter: true,
      Cell: ({ row, table }) => renderConversationNameColumn(row.original, table),
    }),
    columnHelper.accessor(getForumCreatedDateSortValue, {
      header: "Created",
      id: "createdDate",
      meta: { kind: "timestamp" },
      enableSorting: true,
      enableColumnFilter: false,
      // The search reads a conversation's name, type and context, never its date (#6590).
      enableGlobalFilter: false,
      Cell: ({ row }) => renderCreatedDateColumn(row.original),
    })
  ], [renderConversationNameColumn, renderCreatedDateColumn]);

  function renderForumRowActions(forum: Forum): React.ReactNode {
    const forumTitle = getForumChatTitle(forum, session.user?.fullName);
    return (
      <RowActions
        ariaLabel={`Actions for ${forumTitle}`}
        actions={[
          {
            key: "view",
            label: "View Conversation",
            ariaLabel: `View conversation ${forumTitle}`,
            icon: <PageviewIcon fontSize="medium" />,
            onClick: () => navigate(conversationPath(forum)),
          },
        ]}
      />
    );
  }

  const tableOptions: MRT_TableOptions<Forum> = {
    columns,
    data: forums ?? [],
    // The pager's page count comes from every conversation the query matches, which the API sends
    // in X-Pagination. The length of the page held here made the first page look like the last.
    rowCount,
    enableRowSelection: false,
    enableRowActions: true,
    enableTableHead: true,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        muiTableHeadCellProps: {
          sx: { px: 0.5 },
        },
        muiTableBodyCellProps: {
          align: "center",
          sx: {
            px: 0.5,
          },
        },
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search conversations by name or project",
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: globalFilter ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear search" size="small" onClick={() => setGlobalFilter("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    renderRowActions: ({ row }) => renderForumRowActions(row.original),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        void navigateToTarget(row.original);
      },
      sx: {
        cursor: "pointer",
        backgroundColor: "inherit",
      },
    }),
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMedDisplaySize && (
          <ListPageToolbarButtons>
            <ToolbarButtons>
              <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
              <TableToolsMenu table={table} />
            </ToolbarButtons>
            <ToolbarActionButtons>
              <TableActionButton
                color="primary"
                startIcon={<AddCommentIcon />}
                onClick={onCompose}
              >
                New Message
              </TableActionButton>
            </ToolbarActionButtons>
          </ListPageToolbarButtons>
        )}
      </>
    ),
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    // The table had inherited the communications page's copy along with its illustration, and told
    // anyone who searched it that "No matching communications found" — on a page of conversations.
    renderEmptyRowsFallback: emptyRowsFallback({
      // A glyph, not the "All caught up" illustration, which drew a second message above this one (#6099).
      icon: <ForumOutlinedIcon />,
      isError: Boolean(errorLoadingQuery),
      isLoading: queryIsLoading,
      onRetry: () => {
        if (forumQuery) dispatch(populateForumsByQuery({ session, query: forumQuery }));
      },
      subject: "conversations",
    }),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
      columnOrder: ["name", "createdDate", "actions"],
      showGlobalFilter: true,
    },
    manualFiltering: true,
    enableColumnFilters: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnPinning: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: { sx: { flex: 1, minWidth: 0 } },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: {
      sx: {
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
        "& > .MuiBox-root": {
          gap: 0,
          minWidth: 0,
          overflow: "hidden",
        },
      },
    },
    muiTableBodyProps: {
      sx: {
        height: "100%",
        "& tr:only-of-type td[colspan]": {
          height: "100%",
          verticalAlign: "middle",
          padding: 0,
          borderBottom: "none",
        },
      },
    },
    muiToolbarAlertBannerProps: showAlertBanner
      ? {
          color: "error",
          title: errorLoadingQuery || "",
        }
      : {
          action: <AlertActions />,
        },
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    positionGlobalFilter: "none",
    state: {
      columnFilters,
      globalFilter,
      isLoading: queryIsLoading,
      pagination,
      showAlertBanner,
      showProgressBars,
      sorting,
      rowSelection,
      showSkeletons,
    },
  }

  if (!sessionPermissions) return permissionsLoadState;
  if (!sessionPermissions.includesAccessToCommunications) return <NotAuthorized />;

  return <MRTNoMemo<Forum> options={tableOptions} persistColumnVisibilityAs="forums" cards />;
}

/**
 * Where a row opens: a work agreement's or a proposal's conversation on that record's page, at the
 * oldest unread message (#6585); any other conversation in the chat dialog.
 */
function conversationPath(forum: Forum): string {
  return (
    recordConversationPath({
      forumId: String(forum.id),
      topicClass: forum.topic?.entityClass?.value,
      topicId: forum.topic?.entityId?.value,
    }) ?? `${CONVERSATIONS_PATH}/${forum.id}`
  );
}

function getForumNameFilterValue(forum: Forum): string {
  return forum.name?.toString() ?? "";
}

function getForumCreatedDateSortValue(forum: Forum): number {
  return forum.createdDate?.value ?? 0;
}

function getForumOrderField(columnId: string): ForumOrderField {
  if (columnId === "name") return ForumOrderField.Name;
  return ForumOrderField.CreatedDate;
}
